"use client"
import { PlayIcon } from "@/components/icons/pixel-icons"
import { WalletButton } from "@/components/wallet/wallet-button"
import { Trophy } from "lucide-react"
import { LeaderboardModal } from "@/components/leaderboard/leaderboard-modal"
import { useViewportHeight } from "@/hooks/use-viewport-height"
import { usePhasedAssetLoader } from "@/hooks/use-phased-asset-loader"
import { NineSliceButton } from "@/components/ui/nine-slice-button"
import { LoadingOverlay } from "@/components/ui/loading-overlay"
import { useScreenMusic } from "@/hooks/use-screen-music"
import { useServiceWorker } from "@/hooks/use-service-worker"
import { HamburgerMenuButton } from "@/components/game-ui/hud/hamburger-menu-button"
import { HUD_ICON_BUTTON_CLASS } from "@/components/game-ui/hud/hud-button-style"
import { GameMenu } from "@/components/game-ui/menu/game-menu"
import { TutorialModal } from "@/components/game-ui/tutorial/tutorial-modal"
import { useTutorial } from "@/hooks/use-tutorial"
import { useState, useEffect } from "react"
import type { PersistedGameState } from "@/services/game-state-persistence"
import { levels } from "@/data/levels"
import { heroTypes } from "@/data/hero-types"

interface TitleScreenProps {
  onStartGame: () => void
  isMobile?: boolean
  /** Recoverable run snapshot (crash / evicted tab), offered before Start */
  pendingRun?: PersistedGameState | null
  onResumeRun?: () => void
  onDiscardRun?: () => void
}

export const TitleScreen = ({ onStartGame, isMobile = false, pendingRun = null, onResumeRun, onDiscardRun }: TitleScreenProps) => {
  const [showButton, setShowButton] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false)
  const { isTutorialOpen, openTutorial, closeTutorial } = useTutorial()
  const viewportHeight = useViewportHeight()

  // Register service worker for asset caching
  useServiceWorker()

  // Use phased asset loader
  const { state, loadPhase1, loadPhase2, isPhaseComplete } = usePhasedAssetLoader()

  // Play menu music on title screen
  useScreenMusic({
    musicUrl: "/sounds/events/menu.ogg",
    isActive: true,
  })

  // Load Phase 1 (title screen essentials) immediately
  useEffect(() => {
    loadPhase1()
  }, [loadPhase1])

  // Load Phase 2 (droppod assets) after Phase 1 completes
  useEffect(() => {
    if (isPhaseComplete(1)) {
      loadPhase2()
    }
  }, [isPhaseComplete, loadPhase2])

  // Show START button after Phase 2 completes
  const isReadyToStart = isPhaseComplete(1) && isPhaseComplete(2)

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isReadyToStart) {
        setShowButton(true)
      }
    }, 100)
    return () => clearTimeout(timer)
  }, [isReadyToStart])

  useEffect(() => {
    if (isReadyToStart) {
      setShowButton(true)
    }
  }, [isReadyToStart])

  // Calculate combined progress for Phase 1 + Phase 2
  const totalLoaded = state.phase1.loaded + state.phase2.loaded
  const totalAssets = state.phase1.total + state.phase2.total
  const combinedProgress = totalAssets > 0 ? Math.round((totalLoaded / totalAssets) * 100) : 0

  return (
    <div
      id="title-screen"
      className="absolute inset-0 flex flex-col items-center"
      style={{ height: viewportHeight ? `${viewportHeight}px` : "100dvh" }}
    >
      <div className="absolute inset-0 w-full h-full z-0">
        <img
          src="/images/mainscreen-bg.png"
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ imageRendering: "pixelated" }}
        />
        <div className="absolute inset-0 bg-background/40" />
      </div>

      {/* Main menu button - top left */}
      <div className="absolute top-4 left-4 z-30">
        <HamburgerMenuButton onClick={() => setIsMenuOpen(true)} />
      </div>

      {/* Leaderboard + wallet buttons - top right */}
      <div className="absolute top-4 right-4 z-30 flex items-center gap-2">
        <button
          id="leaderboard-button"
          onClick={() => setIsLeaderboardOpen(true)}
          className={HUD_ICON_BUTTON_CLASS}
          title="Leaderboard"
          aria-label="Open leaderboard"
        >
          <Trophy size={18} />
        </button>
        <div className="relative">
          <WalletButton />
        </div>
      </div>

      <LeaderboardModal isOpen={isLeaderboardOpen} onClose={() => setIsLeaderboardOpen(false)} />

      <div className="flex-1 flex items-center justify-center pt-16 z-10">
        <img
          src="/images/pelf-logo.png"
          alt="PELF"
          className="w-auto h-48 md:h-60 mx-auto pixelated"
          style={{ imageRendering: "pixelated" }}
        />
      </div>

      <div
        className={`w-full flex flex-col items-center justify-center gap-3 pb-6 transition-opacity duration-300 z-10 ${
          showButton ? "opacity-100" : "opacity-0"
        }`}
      >
        {pendingRun && onResumeRun && (
          <div className="flex flex-col items-center gap-2 mb-1">
            {/* Run summary chip - same translucent plate as the HUD */}
            <div className="bg-black/60 text-white rounded-lg px-4 py-2 text-center">
              <div className="text-amber-300 text-xs font-bold uppercase tracking-wider mb-0.5">
                Run in progress
              </div>
              <div className="text-sm font-semibold">
                {levels[pendingRun.currentLevelIndex]?.name ?? "Unknown sector"}
                {" — wave "}
                {pendingRun.currentWaveIndex + 1}/{levels[pendingRun.currentLevelIndex]?.waves.length ?? "?"}
              </div>
              <div className="text-xs text-white/70">
                {pendingRun.hero?.name ?? heroTypes[pendingRun.selectedHeroType]?.name ?? "Hero"}
                {" · Lv "}
                {pendingRun.hero?.level ?? 1}
                {" · "}
                {pendingRun.stats.money}g
              </div>
            </div>
            <NineSliceButton id="resume-run-button" onClick={onResumeRun} className="text-base" disabled={!isReadyToStart}>
              <span className="text-black">RESUME RUN</span>
              <PlayIcon size={20} />
            </NineSliceButton>
            <button
              id="discard-run-button"
              onClick={onDiscardRun}
              className="text-white/60 hover:text-white/90 text-xs underline underline-offset-2 transition-colors"
            >
              Discard run
            </button>
          </div>
        )}
        <NineSliceButton id="start-game-button" onClick={onStartGame} className="text-base" disabled={!isReadyToStart}>
          <span className="text-black">{pendingRun ? "NEW GAME" : "START GAME"}</span>
          <PlayIcon size={20} />
        </NineSliceButton>
      </div>

      {/* Loading overlay - shows if loading takes > 200ms */}
      <LoadingOverlay
        progress={combinedProgress}
        isComplete={isReadyToStart}
        minDelayMs={200}
      />

      {/* Main menu */}
      <GameMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        isMobile={isMobile}
        onOpenTutorial={openTutorial}
        pendingRun={pendingRun}
        onResumeRun={onResumeRun}
        onDiscardRun={onDiscardRun}
      />

      {/* Tutorial modal */}
      <TutorialModal
        isOpen={isTutorialOpen}
        onClose={closeTutorial}
      />
    </div>
  )
}
